import React, { useEffect, useRef, useState } from "react";
import { formatTimeMilli } from "components/utils";
import { useRepublishSection } from "providers/RepublishSectionProvider";
import { useIsEnglishUi } from "utils/uiLanguage";
import { cuesRu, pluralRu } from "utils/pluralRu";

/**
 * Сводка блока пересборки над колонками.
 *
 * Переводчику нужно знать не «папка изменилась», а какой кусок дорожки будет заменён: пока он не
 * сдал папку, все правки внутри этого куска бесплатны, а правка за его пределами блок раздвигает.
 * Поэтому здесь же — сообщение о том, что последнее сохранение блок расширило.
 */
export const RepublishSectionBadge = () => {
    const { answer, loading } = useRepublishSection();
    const en = useIsEnglishUi();
    const now = answer?.now;
    const rebuilding = answer?.rebuilding;
    const [grew, setGrew] = useState<string>("");
    const prev = useRef<{ count: number; from: number; to: number } | null>(null);

    useEffect(() => {
        if (!now || now.state !== "dirty") {
            prev.current = now && now.state === "clean" ? { count: 0, from: 0, to: 0 } : prev.current;
            return;
        }
        const next = { count: now.cue_ids.length, from: now.from_ms, to: now.to_ms };
        const was = prev.current;
        prev.current = next;
        if (was && was.count && next.count > was.count) {
            setGrew(
                `блок вырос: было ${was.count} (${formatTimeMilli(was.from)}–${formatTimeMilli(was.to)}), ` +
                    `стало ${next.count} (${formatTimeMilli(next.from)}–${formatTimeMilli(next.to)})`,
            );
        }
    }, [now?.state, now?.cue_ids.length, now?.from_ms, now?.to_ms]);

    if (!answer || !now) return null;

    if (rebuilding && (rebuilding.state === "queued" || rebuilding.state === "running")) {
        return (
            <div className="toolbar-btn" style={{ cursor: "default", borderColor: "#faad14" }}
                 title={en
                     ? "Folder handed in: these cues are already being assembled. Edits to them arrive on the next pass."
                     : "Папка сдана: эти реплики уже собираются. Правки в них приедут следующим кругом."}>
                🟡 {en ? "Rebuilding" : "Пересборка"}: {rebuilding.cue_ids.length}{" "}
                {en ? "cues" : pluralRu(rebuilding.cue_ids.length, "реплика", "реплики", "реплик")}{" "}
                ({formatTimeMilli(rebuilding.from_ms)}–{formatTimeMilli(rebuilding.to_ms)})
            </div>
        );
    }

    if (now.state === "clean") {
        return (
            <div className="toolbar-btn" style={{ cursor: "default", opacity: 0.7 }}
                 title={en
                     ? "The assembled track matches the cues — there is nothing to replace"
                     : "Собранная дорожка совпадает с репликами — заменять нечего"}>
                {en
                    ? "✓ Folder track matches the current cues"
                    : "✓ Дорожка папки собрана по нынешним репликам"}
            </div>
        );
    }

    if (now.state === "no_manifest") {
        return (
            <div className="toolbar-btn" style={{ cursor: "default", opacity: 0.7 }}
                 title={en
                     ? "This folder was assembled before track snapshots existed — nothing to compare against, so the whole folder is replaced"
                     : "Папку собирали до появления снимка дорожки — сравнить не с чем, заменится вся папка"}>
                ⚠ {en
                    ? `The whole folder will be replaced (${now.total_cues} cues)`
                    : `Заменится вся папка (${cuesRu(now.total_cues)})`}
            </div>
        );
    }

    if (now.state !== "dirty") return null;

    return (
        <div
            className="toolbar-btn"
            style={{ cursor: "default", borderColor: "#1677ff" }}
            title={
                en
                    ? `${now.reason}. The section is re-uploaded whole anyway — edits inside it add nothing to the work.`
                    : `${now.reason}. Блок всё равно перезаливается целиком — правки внутри него ничего не добавляют к работе.`
            }
        >
            🔵 {en ? "Rebuild section" : "Блок пересборки"}: {now.cue_ids.length}{" "}
            {en ? "of" : "из"} {now.total_cues} ({formatTimeMilli(now.from_ms)}–
            {formatTimeMilli(now.to_ms)})
            {loading ? (en ? " · counting…" : " · считаю…") : ""}
            {grew ? <span style={{ marginLeft: 8, color: "#faad14" }}>· {grew}</span> : null}
        </div>
    );
};
